/**
 * @file WarehouseDevicesPanel.tsx
 * @description Устройства склада: принтеры, сканеры, весы, кассы (вынесено из
 * `WmsSettingsPage` в phase19.23 этап A).
 *
 * Панель держит запросы и подтверждения; разметку списка рисует `DevicesList`,
 * форму добавления — `DeviceAddForm`. После каждой мутации список
 * перечитывается с сервера, а не правится руками.
 *
 * phase19.31 (зона «Склад»): список на общем `ResponsiveList` (пустое
 * состояние — его), «Добавить устройство» — второстепенная кнопка раздела;
 * «Сохранить» у вкладки нет — всё применяется сразу.
 */

import { Loader2, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

import { DeviceAddForm } from "./DeviceAddForm";
import { DevicesList } from "./DevicesList";
import { WmsSettingsHeader } from "./WmsSettingsHeader";
import { useWmsConfirm } from "../use-wms-confirm";
import { WmsConfirmDialog } from "../WmsConfirmDialog";
import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  devicesApi,
  type DeviceConfigDto,
  type WmsDeviceKind,
  type WmsDeviceTransport,
} from "../../../lib/api-wms";

interface Props {
  warehouseId: string;
  /** Строка «Склад ▾» — первой строкой (её рисует страница). */
  warehousePicker?: ReactNode;
}

export function WarehouseDevicesPanel({ warehouseId, warehousePicker }: Props) {
  const notify = useNotify();
  const asker = useWmsConfirm();
  const [items, setItems] = useState<DeviceConfigDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await devicesApi.list(warehouseId));
    } catch (err) {
      // Раньше отказ уходил в никуда, и панель молча показывала пустой список.
      notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить устройства");
    } finally {
      setLoading(false);
    }
  }, [warehouseId, notify]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleAdd(input: {
    kind: WmsDeviceKind;
    transport: WmsDeviceTransport;
    name: string;
  }) {
    if (!input.name) return;
    try {
      await devicesApi.create(warehouseId, input);
      setAdding(false);
      notify.success("Устройство добавлено");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  async function handleRemove(id: string) {
    // Окно кабинета вместо системного `confirm()` (phase19.31).
    const ok = await asker.confirm({ title: "Удалить устройство?", action: "Удалить", danger: true });
    if (!ok) return;
    try {
      await devicesApi.remove(warehouseId, id);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  async function handleSetDefault(id: string) {
    try {
      await devicesApi.update(warehouseId, id, { isDefault: true });
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <WmsSettingsHeader />
      <div className="max-w-[45rem]">{warehousePicker}</div>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-primary" />
        </div>
      ) : (
        <DevicesList
          items={items}
          onSetDefault={(id) => void handleSetDefault(id)}
          onRemove={(id) => void handleRemove(id)}
        />
      )}

      {adding ? (
        <DeviceAddForm
          onSubmit={(input) => void handleAdd(input)}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <Button variant="soft" onClick={() => setAdding(true)} className="self-start">
          <Plus className="size-4" />
          Добавить устройство
        </Button>
      )}
      <WmsConfirmDialog request={asker.request} onSettle={asker.settle} />
    </div>
  );
}
